import { useEffect, useState } from 'react';

import { ChevronLeft } from '@gympass/yoga-icons';
import Logo from 'logo/logo.svg';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import useOnInit from 'hooks/useOnInit';

import AccessReady from './Steps/AccessReady';
import CreatePassword from './Steps/CreatePassword';
import InputCredentials from './Steps/InputCredentials';
import SendEmail from './Steps/SendEmail';

import { ACCESS_READY_FLOW } from './Steps/AccessReady/types';
import { LoginProps, LOGIN_STEP } from './types';
// eslint-disable-next-line import/order
import { CognitoUser } from 'types/others';

import * as S from './styles';

const Login: React.FC<LoginProps> = ({ onStateChange }) => {
  const { step: stepParam } = useParams<{
    step: LOGIN_STEP;
  }>();

  const [email, setEmail] = useState('');
  const [user, setUser] = useState<CognitoUser>({});
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [loginStep, setLoginStep] = useState(LOGIN_STEP.INPUT_CREDENTIALS);

  const handleFirstLoginFlow = (loginUser: CognitoUser) => {
    setUser(loginUser);
    setIsFirstLogin(true);
    setLoginStep(LOGIN_STEP.CREATE_PASSWORD);
  };

  const handleEmailSent = (recoveryEmail: string) => {
    setEmail(recoveryEmail);
    setLoginStep(LOGIN_STEP.CREATE_PASSWORD);
  };

  const handleAccessReady = () => {
    if (!isFirstLogin) {
      setLoginStep(LOGIN_STEP.INPUT_CREDENTIALS);
    }
  };

  const handleOnStateChange = (state: string, cognitoUser?: CognitoUser) => {
    if (onStateChange) {
      onStateChange(state, cognitoUser);
    }
  };

  useOnInit(() => {
    if (stepParam) {
      const availableSteps = Object.values(LOGIN_STEP);

      if (availableSteps.includes(stepParam)) {
        setLoginStep(stepParam);
      }
    }
  });

  useEffect(() => {
    const initializeLoginFlow = () => {
      setLoginStep(LOGIN_STEP.INPUT_CREDENTIALS);
    };

    window.history.pushState(null, '', document.URL);

    window.addEventListener('popstate', () => {
      initializeLoginFlow();
    });

    return () => {
      window.removeEventListener('popstate', () => {
        initializeLoginFlow();
      });
    };
  }, []);

  return (
    <S.Container data-testid="contentLogin">
      <S.SubContainer>
        {!isFirstLogin &&
          loginStep !== LOGIN_STEP.INPUT_CREDENTIALS &&
          loginStep !== LOGIN_STEP.ACCESS_READY && (
            <S.MobileNavigationHeader
              onClick={() => {
                setLoginStep(LOGIN_STEP.INPUT_CREDENTIALS);
              }}
            >
              <ChevronLeft width={26} height={26} />

              <S.NavigationButtonLabel>
                <FormattedMessage id="LOGIN.NAVIGATION_HEADER.LABEL" />
              </S.NavigationButtonLabel>
            </S.MobileNavigationHeader>
          )}
        <S.ImageContainer
          displayInMobile={
            (isFirstLogin || loginStep === LOGIN_STEP.INPUT_CREDENTIALS) &&
            loginStep !== LOGIN_STEP.ACCESS_READY
          }
        >
          <Logo />
        </S.ImageContainer>
        <S.LoginCard>
          <S.LoginSubCard>
            {loginStep === LOGIN_STEP.INPUT_CREDENTIALS && (
              <InputCredentials
                onSetNewLogin={handleFirstLoginFlow}
                onStateChange={handleOnStateChange}
                onTemporaryPasswordRecovery={() =>
                  setLoginStep(LOGIN_STEP.TEMPORARY_PASSWORD_SENT)
                }
                onPasswordRecovery={() => setLoginStep(LOGIN_STEP.SEND_EMAIL)}
              />
            )}
            {loginStep === LOGIN_STEP.SEND_EMAIL && (
              <SendEmail onEmailSent={handleEmailSent} />
            )}
            {loginStep === LOGIN_STEP.CREATE_PASSWORD && (
              <CreatePassword
                user={user}
                email={email}
                isFirstLogin={isFirstLogin}
                onPasswordCreated={() => setLoginStep(LOGIN_STEP.ACCESS_READY)}
              />
            )}
            {loginStep === LOGIN_STEP.ACCESS_READY && (
              <AccessReady
                onAccessReady={handleAccessReady}
                accessReadyFlow={
                  isFirstLogin
                    ? ACCESS_READY_FLOW.FIRST_LOGIN
                    : ACCESS_READY_FLOW.NORMAL_LOGIN
                }
              />
            )}
            {loginStep === LOGIN_STEP.TEMPORARY_PASSWORD_SENT && (
              <AccessReady
                onAccessReady={handleAccessReady}
                accessReadyFlow={ACCESS_READY_FLOW.TEMPORARY_PASSWORD_RECOVERY}
              />
            )}
          </S.LoginSubCard>
        </S.LoginCard>
      </S.SubContainer>
    </S.Container>
  );
};

export default Login;
